import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import withRouter from '../../hooks/withRouter';

import Activities from 'activities';
import styles from './Tester.module.scss';
import { setTesterActivity, resetActivityStore, setLevelOverride } from 'store/actions/activity';
import { setLocale } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import { setAppMode, setModule, hideForwardButton } from 'store/actions/ui';

const ACTIVITIES_BY_APP = {
  letters: [
    'FlipCard',
    'StartingSound',
    'MatchItTrashIt',
    'PuzzlePieces',
    'SoundRecorder',
    'SoundRepeater',
    'WallCard',
    'WordRecorder',
    'WordRepeater'
  ],
  numbers: [
    'Counters',
    'CrayonStroke',
    'FlipCard',
    'KnowMyNumbers',
    'MatchItTrashIt',
    'TagBags',
    'VoiceRecorder',
    'VoiceRepeater'
  ],
  'a-z-mat-man': ['DidYouKnow', 'WallCard']
};

export class Tester extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activityId: 0,
      module: 'letters',
      selectedActivity: null,
      error: false
    };
  }

  componentDidMount() {
    const {
      props: { hideForwardButton, setAppMode, setModule, resetActivityStore }
    } = this;

    setAppMode('tester');
    setModule('letters');
    resetActivityStore();
    hideForwardButton();
  }

  redirectToActivity = () => {
    const {
      props: {
        activityData: { activities },
        router
      }
    } = this;
    const { module } = this.state;
    const { type } = activities[0];

    router.navigate(`/${module}/activity/${type}`);
  };

  launchActivity = async e => {
    e.preventDefault();

    this.setState({ error: false }, async () => {
      const { setTesterActivity, module, levelOverride } = this.props;
      const { selectedActivity, activityId, module: receivedModule } = this.state;

      if (!module || module === 'login') return;

      const status = await setTesterActivity({
        activityId,
        template: selectedActivity,
        ...(levelOverride && { level: levelOverride }),
        receivedModule: receivedModule || module
      });

      if (status < 400) {
        this.redirectToActivity();
      } else {
        this.setState({ error: true });
      }
    });
  };

  handleChange = e => this.setState({ activityId: e.target.value });

  handleSelectChange = e => {
    if (e.target.name === 'module') {
      this.props.setModule(e.target.value);
      this.setState({ module: e.target.value });
    }

    if (e.target.name === 'levelOverride') this.props.setLevelOverride(parseInt(e.target.value));
    if (e.target.name === 'locale') setLocale(e.target.value);
  };

  selectActivity(activity) {
    this.setState(({ selectedActivity }) => ({
      error: false,
      selectedActivity: selectedActivity === activity ? null : activity
    }));
  }

  render() {
    const activitiesToRender = Object.keys(Activities);
    const {
      state: { module, activityId, selectedActivity },
      props: { levelOverride, locale },
      handleSelectChange,
      launchActivity
    } = this;

    return (
      <div className={styles.Tester}>
        <div className={styles.header}>
          <form className={styles.testerForm} onSubmit={launchActivity}>
            <label className={styles.headerLabel}>
              Module:
              <select
                onChange={e => handleSelectChange(e)}
                value={module}
                name="module"
                required
                className={styles.headerSelect}
                id="select-module"
              >
                {Object.keys(ACTIVITIES_BY_APP).map((key, index) => (
                  <option value={key} key={`module-option-${index}`}>
                    {key}
                  </option>
                ))}
              </select>
            </label>

            <label className={styles.headerLabel}>
              Level Override:
              <select
                onChange={e => handleSelectChange(e)}
                value={levelOverride}
                className={styles.headerSelect}
                name="levelOverride"
                id="level-override"
              >
                <option value="0">None</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
              </select>
            </label>

            <label className={styles.headerLabel}>
              Locale:
              <select
                onChange={e => handleSelectChange(e)}
                value={locale}
                className={styles.headerSelect}
                name="locale"
                id="select-locale"
              >
                <option value="en-US">en-US</option>
                <option value="es-US">es-US</option>
              </select>
            </label>

            <label className={styles.headerLabel}>
              Activity Id:
              <input
                type="number"
                className={styles.headerInput}
                onChange={e => this.handleChange(e)}
                id="search-activity-number"
              ></input>
            </label>
            <button
              type="submit"
              className={[
                'gss-button',
                styles.headerButton,
                !activityId && !selectedActivity ? styles.disabled : ''
              ].join(' ')}
              disabled={!activityId && !selectedActivity}
              id="tester-go-button"
            >
              Go!
            </button>
          </form>
        </div>

        <div className={styles.activityList}>
          <h2 className={styles.listTitle}>
            Activity Types{' '}
            {this.state.error && (
              <span className={styles.errorNotFound}>No activity found for this combination</span>
            )}
          </h2>
          <div className={styles.listContainer} id="activity-container">
            {activitiesToRender.map(
              activity =>
                ACTIVITIES_BY_APP[this.state.module].includes(activity) && (
                  <div
                    className={[
                      selectedActivity === activity ? styles.selected : '',
                      styles.activity
                    ].join(' ')}
                    key={activity}
                    onClick={() => this.selectActivity(activity)}
                  >
                    {activity}
                  </div>
                )
            )}
          </div>
        </div>
      </div>
    );
  }
}

Tester.propTypes = {
  activityData: PropTypes.shape({
    activities: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string
      })
    )
  }),
  setTesterActivity: PropTypes.func.isRequired,
  router: PropTypes.object,
  levelOverride: PropTypes.number,
  loading: PropTypes.bool,
  module: PropTypes.string,
  locale: PropTypes.string,
  resetActivityStore: PropTypes.func.isRequired,
  setAppMode: PropTypes.func.isRequired,
  setLevelOverride: PropTypes.func.isRequired,
  setModule: PropTypes.func.isRequired,
  hideForwardButton: PropTypes.func.isRequired
};

export const mapStateToProps = state => ({
  activityData: state.activity.data,
  loading: state.ui.loading,
  levelOverride: state.activity.levelOverride,
  locale: state.ui.locale,
  module: state.ui.module
});

export default connect(mapStateToProps, {
  setTesterActivity,
  resetActivityStore,
  setAppMode,
  setModule,
  setLevelOverride,
  hideForwardButton
})(withRouter(Tester));
